export type Industry = {
  slug: string;
  category?: "manufacturing";
  name: string;
  kicker: string;
  headline: string;
  summary: string;
  audience: string;
  image: string;
  imageAlt: string;
  focus: string[];
  points: [string, string][];
  prepare: string;
  productName: string;
  productPath: string;
  solutionNote: string;
};

export const industries: Industry[] = [
  {
    "slug": "edible-oil-manufacturing",
    "category": "manufacturing",
    "name": "Edible oil manufacturing",
    "kicker": "From receipt to packed oil",
    "headline": "Follow materials from the oilseed or oil receipt to dispatch.",
    "summary": "Oil businesses need visibility across purchasing, storage, processing, packing, and sales. Map the records for your own process—whether crushing, refining, blending, or packing—and connect the operational quantities with stock and costs.",
    "audience": "Oilseed processors, edible oil refiners, blenders, packers, and oil trading businesses",
    "image": "/static/img/industries/edible-oil.jpg",
    "imageAlt": "Illustration of an edible oil bottling line with golden cooking oil and stainless steel tanks",
    "focus": [
      "Batch & tank records",
      "Yield & by-products",
      "Packing & dispatch"
    ],
    "points": [
      [
        "Raw materials and storage",
        "Review oilseed or crude-oil receipts, supplier lots, tank or warehouse stock, and the units used for purchasing and production."
      ],
      [
        "Processing and yield",
        "Define input and output records for each processing stage, including by-products and process loss where relevant."
      ],
      [
        "Quality and packing",
        "Discuss quality-check records, batch references, packaging-material consumption, and the different pack sizes you sell."
      ],
      [
        "Cost and dispatch",
        "Review batch or production costing requirements, finished-goods availability, dispatch records, and customer invoicing."
      ]
    ],
    "prepare": "Bring a material receipt, production or batch sheet, tank-stock record, packaging list, and a sample dispatch invoice.",
    "productName": "SAP Business One",
    "productPath": "/products/sap",
    "solutionNote": "Review the workflow with LITPL before choosing the software. Core finance, purchasing, inventory, and sales can form the ERP foundation; sector-specific processing, quality, costing, and equipment connections may need configuration, add-ons, or custom development."
  },
  {
    "slug": "cement-manufacturing",
    "category": "manufacturing",
    "name": "Cement manufacturing",
    "kicker": "Materials, production & dispatch",
    "headline": "Bring production quantities and dispatch records into focus.",
    "summary": "Cement operations involve bulk materials, production stages, storage, and bagged or bulk dispatch. A useful software scope connects the commercial records with the quantities and operational information your plant needs to review.",
    "audience": "Cement manufacturers, grinding units, bagging operations, and building-material suppliers",
    "image": "/static/img/industries/cement.jpg",
    "imageAlt": "Illustration of cement silos, conveyors, bagged cement, and a dispatch area",
    "focus": [
      "Bulk materials",
      "Bagged & bulk stock",
      "Dispatch & billing"
    ],
    "points": [
      [
        "Purchasing and receipts",
        "Map the receipt and consumption of raw materials, additives, fuel, and packaging materials relevant to your plant."
      ],
      [
        "Production records",
        "Review production-stage quantities, finished output, stock transfers, and the information needed for cost analysis."
      ],
      [
        "Packing and dispatch",
        "Define bagged and bulk stock records, loading documents, vehicle details, and dispatch-to-invoice checks."
      ],
      [
        "Plant connections",
        "Discuss weighbridge, production-system, maintenance, and reporting interfaces separately so responsibilities and data sources are clear."
      ]
    ],
    "prepare": "Bring material and production reports, a dispatch slip, a weighbridge sample, and an example of the costing report you need.",
    "productName": "SAP Business One",
    "productPath": "/products/sap",
    "solutionNote": "Review the workflow with LITPL before choosing the software. Core finance, purchasing, inventory, and sales can form the ERP foundation; sector-specific processing, quality, costing, and equipment connections may need configuration, add-ons, or custom development."
  },
  {
    "slug": "steel-manufacturing",
    "category": "manufacturing",
    "name": "Steel manufacturing",
    "kicker": "Grades, sizes & material movement",
    "headline": "Keep the material identity with the production record.",
    "summary": "Steel businesses work with materials that differ by grade, size, weight, and processing stage. Discuss how raw materials, work in progress, finished products, and scrap should be recorded across your operation.",
    "audience": "Steel manufacturers, rolling mills, fabricators, metal processors, and steel traders",
    "image": "/static/img/industries/steel.jpg",
    "imageAlt": "Illustration of a steel rolling mill with hot billets, finished coils, and beams",
    "focus": [
      "Grades & dimensions",
      "Production & scrap",
      "Weight-based dispatch"
    ],
    "points": [
      [
        "Item and material records",
        "Define the grades, dimensions, units, and heat or lot references your team needs to identify material."
      ],
      [
        "Production and conversion",
        "Map material issues, intermediate stages, finished output, cutting or rolling loss, and scrap records relevant to your process."
      ],
      [
        "Stock and dispatch",
        "Review stock by location and material specification, with the weight and quantity information needed for delivery and billing."
      ],
      [
        "Quality and costing",
        "Discuss inspection records, certificate references, material costs, and the reports required to review production performance."
      ]
    ],
    "prepare": "Bring your item specifications, a production sheet, a stock report, a quality-document sample, and a dispatch invoice.",
    "productName": "SAP Business One",
    "productPath": "/products/sap",
    "solutionNote": "Review the workflow with LITPL before choosing the software. Core finance, purchasing, inventory, and sales can form the ERP foundation; sector-specific processing, quality, costing, and equipment connections may need configuration, add-ons, or custom development."
  },
  {
    "slug": "food-processing",
    "category": "manufacturing",
    "name": "Food processing",
    "kicker": "Ingredients to finished packs",
    "headline": "Connect the ingredient record to the finished batch.",
    "summary": "Food manufacturers need to coordinate ingredients, production, packaging, and dispatch. Start with how you identify materials and batches, then define the records needed to review stock, shelf life, and production costs.",
    "audience": "Packaged-food producers, food processors, bakeries, and businesses with batch-based production",
    "image": "/static/img/industries/food-processing.jpg",
    "imageAlt": "Illustration of a hygienic food production and packaging line",
    "focus": [
      "Ingredients & batches",
      "Shelf-life records",
      "Packing & costing"
    ],
    "points": [
      [
        "Ingredients and recipes",
        "Discuss ingredient records, production recipes or bills of materials, quantities, and substitutions your process requires."
      ],
      [
        "Batch and shelf-life information",
        "Define batch references, date records, stock rotation, and traceability needs across receipts, production, and dispatch."
      ],
      [
        "Production and packaging",
        "Map ingredient consumption, finished output, packing materials, rejects, and other production losses."
      ],
      [
        "Quality and customer dispatch",
        "Review quality-check records, release decisions, customer orders, and the documents that accompany deliveries."
      ]
    ],
    "prepare": "Bring a recipe or bill of materials, a batch sheet, product-date labels, a quality-check form, and an invoice.",
    "productName": "SAP Business One",
    "productPath": "/products/sap",
    "solutionNote": "Review the workflow with LITPL before choosing the software. Core finance, purchasing, inventory, and sales can form the ERP foundation; sector-specific processing, quality, costing, and equipment connections may need configuration, add-ons, or custom development."
  },
  {
    "slug": "fleet-and-transport",
    "name": "Transport & fleet solutions",
    "kicker": "Vehicle records",
    "headline": "Keep the history of each vehicle in one place.",
    "summary": "Keep vehicle profiles, maintenance, tyre history, loading, and loss records together. LITWay is a starting point for truck owners and transport teams; review any additional trip, fuel, freight-billing, or tracking requirements as part of the scope.",
    "audience": "Truck owners, transporters, and teams that maintain their own vehicles",
    "points": [
      [
        "Vehicle records and identity",
        "Organize the vehicle register and its operational history using one vehicle reference, so workshop and operations teams can locate the same records."
      ],
      [
        "Maintenance and tyre history",
        "Keep maintenance work and tyre records together to review previous interventions and recurring issues before planning further work."
      ],
      [
        "Loading and loss records",
        "Link loading activity and reported losses to the relevant vehicle, with the supporting references needed for review and follow-up."
      ],
      [
        "Operational review",
        "Define the vehicle-wise activity summaries your team needs, and separately assess trip, fuel, freight-billing, or tracking requirements."
      ]
    ],
    "prepare": "Bring a vehicle list, one maintenance record, tyre details, and a sample loading document.",
    "productName": "LITWay",
    "productPath": "/products/lit-way",
    "image": "/static/img/industries/transport.jpg",
    "imageAlt": "Illustration of freight trucks at a distribution warehouse",
    "focus": [
      "Vehicle profiles",
      "Maintenance & tyres",
      "Loading records"
    ],
    "solutionNote": "Discuss your operating requirements with LITPL to confirm product fit, configuration, integrations, and implementation scope."
  },
  {
    "slug": "agriculture",
    "name": "Agriculture & agribusiness",
    "kicker": "Inputs, harvest & trading",
    "headline": "See the business behind each crop cycle.",
    "summary": "Agricultural businesses manage input purchases, field activities, harvested produce, storage, and sales. Map those records around your crop cycles and locations so the operational and financial information can be reviewed together.",
    "audience": "Farms, agricultural enterprises, producer organizations, aggregators, and agri-trading businesses",
    "image": "/static/img/industries/agriculture.jpg",
    "imageAlt": "Illustration of crop fields, a tractor, grain sacks, and storage silos",
    "focus": [
      "Crop-cycle costs",
      "Produce inventory",
      "Procurement & sales"
    ],
    "points": [
      [
        "Inputs and purchases",
        "Discuss seed, fertilizer, equipment, and other input records, including the locations or activities against which they are used."
      ],
      [
        "Season and activity costs",
        "Define field or crop-cycle records for labour, irrigation, machinery use, and other costs relevant to the business."
      ],
      [
        "Harvest and storage",
        "Review produce receipts, quantities, grades, lots, storage locations, and stock movements."
      ],
      [
        "Sales and settlements",
        "Connect purchasing or grower records, customer sales, deliveries, invoices, and payment follow-up."
      ]
    ],
    "prepare": "Bring crop or farm records, an input-purchase sample, harvest quantities, storage records, and examples of your sales or settlement documents.",
    "productName": "ERP & custom workflows",
    "productPath": "/services#erp-implementation",
    "solutionNote": "Agriculture requirements need a discovery discussion. ERP can provide a purchasing, stock, sales, and accounts foundation; field records, crop-cycle costing, grower settlements, and specialized tracking must be scoped separately."
  },
  {
    "slug": "rental-services",
    "name": "Rental services",
    "kicker": "Agreements, recurring billing & collections",
    "headline": "Keep every rental linked to its agreement and invoice.",
    "summary": "Rental businesses need a clear record of what is rented, to whom, for which period, and at what charge. Map your agreement, billing, and collection process for commercial property, spaces, equipment, or other rentable assets.",
    "audience": "Commercial property owners, equipment-rental businesses, and service companies with recurring rental income",
    "image": "/static/img/industries/rental.jpg",
    "imageAlt": "Illustration of commercial rental property, equipment, a key, and agreement paperwork",
    "focus": [
      "Assets & agreements",
      "Recurring billing",
      "Deposits & dues"
    ],
    "points": [
      [
        "Asset and customer records",
        "Define the rentable units or equipment, availability information, customers, and agreement references you need to maintain."
      ],
      [
        "Rental terms",
        "Review billing periods, rates, deposits, renewal dates, and any agreed changes in charges."
      ],
      [
        "Billing and collections",
        "Map recurring invoices, additional service charges, payment receipts, and outstanding balances."
      ],
      [
        "Returns and closure",
        "Discuss handover or return records, adjustments, deposits, and closing an agreement without losing its history."
      ]
    ],
    "prepare": "Bring a sample rental agreement, asset or unit list, billing schedule, invoice, and deposit or outstanding-balance report.",
    "productName": "ERP & rental workflows",
    "productPath": "/services#erp-implementation",
    "solutionNote": "Confirm recurring billing, agreement management, availability, deposits, and return workflows during discovery. The final solution may combine ERP configuration with an add-on or custom application."
  },
  {
    "slug": "storage-services",
    "name": "Storage & warehousing services",
    "kicker": "Customer stock, space & storage charges",
    "headline": "Know what is stored—and how it should be billed.",
    "summary": "Storage service providers must distinguish customer-owned goods, receipt and release dates, occupied space, and agreed charges. Connect the stock record with the commercial rules behind each storage agreement.",
    "audience": "Warehouse and godown operators, storage-service providers, and businesses storing goods for customers",
    "image": "/static/img/industries/storage.jpg",
    "imageAlt": "Illustration of warehouse pallet racks, stored goods, and a forklift",
    "focus": [
      "Customer-wise stock",
      "Receipts & releases",
      "Storage-charge billing"
    ],
    "points": [
      [
        "Receipts and ownership",
        "Review inward documents, customer or owner references, item quantities, lots, and storage locations."
      ],
      [
        "Movement and balances",
        "Define the record of internal transfers, outward releases, adjustments, and customer-wise stock balances."
      ],
      [
        "Storage-charge rules",
        "Discuss the agreed billing basis—such as quantity, weight, occupied space, or time—and any handling or other service charges."
      ],
      [
        "Invoices and reconciliation",
        "Map periodic billing, release-related charges, customer statements, and checks between stock movements and invoices."
      ]
    ],
    "prepare": "Bring inward and outward documents, a customer-stock report, a rate agreement, and one worked example of a storage bill.",
    "productName": "ERP & storage workflows",
    "productPath": "/services#erp-implementation",
    "solutionNote": "Storage billing depends on your agreements and operating rules. Confirm charge calculations, customer ownership, warehouse interfaces, and any specialized storage monitoring before defining the solution."
  },
  {
    "slug": "poultry-farming",
    "name": "Poultry farming",
    "kicker": "Flocks, feed & farm accounts",
    "headline": "Bring daily flock records into a clearer business view.",
    "summary": "Poultry farms keep daily records of birds, feed, farm expenses, and production. Organize those records by flock or batch to support purchasing, stock control, sales, and financial review across your farm.",
    "audience": "Broiler farms, layer farms, and poultry businesses managing flock and farm records",
    "image": "/static/img/industries/poultry.jpg",
    "imageAlt": "Illustration of a clean poultry house with white chickens, feed silos, and egg trays",
    "focus": [
      "Flock & batch records",
      "Feed & farm costs",
      "Egg or bird sales"
    ],
    "points": [
      [
        "Flock or batch register",
        "Discuss placement dates, opening bird numbers, houses or sheds, bird movements, and mortality records."
      ],
      [
        "Feed and farm expenses",
        "Review feed purchases, stock and consumption records, labour, utilities, and other costs allocated to the flock or farm."
      ],
      [
        "Daily production records",
        "Define the egg-production records needed for layers or the weight and sale records relevant to broiler operations."
      ],
      [
        "Sales and accounts",
        "Connect egg or bird sales, customer invoices, receipts, purchases, and the financial reports the business needs."
      ]
    ],
    "prepare": "Bring a flock register, daily feed and production sheet, farm-expense records, and sample purchase and sales documents.",
    "productName": "ERP & poultry workflows",
    "productPath": "/services#software-development",
    "solutionNote": "Define flock, feed, production, accounting, and reporting requirements together, including any farm-system integrations, before confirming the application and implementation scope."
  },
  {
    "slug": "field-distribution",
    "name": "Field distribution",
    "kicker": "Roadside sales",
    "headline": "Follow a field sale through to the receipt.",
    "summary": "Distributors lose track of a day when the bill, the cash collected, the expense, and the person who made the sale sit in different places. LITFusion is built for that roadside workflow.",
    "audience": "Trading firms and distributors with teams selling on the road",
    "points": [
      [
        "Roadside sales and billing",
        "Record the customer transaction, items, quantities, and bill so the office can review the sale using a consistent reference."
      ],
      [
        "Payment receipts and collections",
        "Keep the receipt associated with the sale or customer it belongs to, and define how the office checks collections against billed activity."
      ],
      [
        "Expenses and employee activity",
        "Capture the expenses and employee activity needed to explain the field team’s working day and support supervisor review."
      ],
      [
        "Daily reconciliation",
        "Agree how bills, receipts, expenses, and unresolved differences are reviewed, including the devices and connectivity needed by your team."
      ]
    ],
    "prepare": "Bring a sample bill, a payment receipt, an expense note, and a short description of how the field team works.",
    "productName": "LITFusion",
    "productPath": "/products/lit-fusion",
    "image": "/static/img/industries/field-distribution.jpg",
    "imageAlt": "Van salesperson with a tablet handing a carton to a shopkeeper at a stocked grocery store while a helper unloads more cartons from the van",
    "focus": [
      "The sale",
      "The collection",
      "The day"
    ],
    "solutionNote": "Discuss your operating requirements with LITPL to confirm product fit, configuration, integrations, and implementation scope."
  },
  {
    "slug": "retail-billing",
    "name": "Retail billing",
    "kicker": "Counter and stock",
    "headline": "Issue the bill and keep the stock record with it.",
    "summary": "A shop slows down when the invoice, the cash, and the available stock are updated in different books. LITKHATA is for shopkeepers and trading counters that need those tasks together.",
    "audience": "Shopkeepers and trading firms handling daily billing and inventory",
    "points": [
      [
        "Item selection and invoices",
        "Use an organized item catalogue and barcode-assisted selection where applicable to prepare customer invoices with the required quantities and prices."
      ],
      [
        "Cash and payment records",
        "Keep cash records alongside the day’s invoices so the team can compare recorded sales and collections during closing."
      ],
      [
        "Inventory visibility",
        "Review stock balances and movements alongside counter activity, with agreed procedures for receipts, returns, and corrections."
      ],
      [
        "Counter tools and data entry",
        "Review barcode equipment, document scanning, and text-to-speech using your own items, labels, and documents to confirm the right setup."
      ]
    ],
    "prepare": "Bring an item list, a sample invoice, barcode labels, and any document you want to scan.",
    "productName": "LITKHATA",
    "productPath": "/products/lit-khata",
    "image": "/static/img/industries/retail-billing.jpg",
    "imageAlt": "Shopkeeper handing a printed bill to a customer at a grocery counter with a billing computer, card machine, and stocked shelves",
    "focus": [
      "Billing",
      "Cash and stock",
      "Easier entry"
    ],
    "solutionNote": "Discuss your operating requirements with LITPL to confirm product fit, configuration, integrations, and implementation scope."
  },
  {
    "slug": "depot-and-containers",
    "name": "Depot and containers",
    "kicker": "ICD and CFS",
    "headline": "Connect container movement with the charge behind it.",
    "summary": "Depot work spans the manifest, handling, storage, dispatch, and the invoice. ICD Management is an SAP Business One add-on for teams that need those steps in one operational flow.",
    "audience": "Port, inland container depot, depot, and container freight station teams",
    "points": [
      [
        "Manifest and container management",
        "Connect manifest and MBL or HBL references with container records, arrival information, and the customer responsible for the activity."
      ],
      [
        "Handling and cargo operations",
        "Scope general and specialized container handling, stuffing, destuffing, and loose-cargo activities around the services your depot performs."
      ],
      [
        "Storage and customer tariffs",
        "Apply customer-group pricing and verify storage periods and service charges using the agreed tariff and operational history."
      ],
      [
        "Dispatch, invoicing, and ERP",
        "Keep dispatch documents, proforma or tax invoices, and empty-container operations connected with the SAP Business One process."
      ]
    ],
    "prepare": "Bring a sample manifest, a tariff sheet, a storage calculation, and details of your SAP Business One environment.",
    "productName": "ICD Management",
    "productPath": "/products/icd",
    "image": "/static/img/industries/container-yard.jpg",
    "imageAlt": "Representative container yard with stacked containers, a reach stacker, truck lanes, gate control, and a supervisor using a tablet",
    "focus": [
      "Manifest",
      "Handling and storage",
      "Charges"
    ],
    "solutionNote": "Discuss your operating requirements with LITPL to confirm product fit, configuration, integrations, and implementation scope."
  },
  {
    "slug": "mining-and-quarrying",
    "name": "Mining & quarrying",
    "kicker": "Extraction, haulage & dispatch",
    "headline": "Connect the pit, the plant, the stockpile, and the sale.",
    "summary": "Mining and quarry operations need to know what was extracted, hauled, crushed, stockpiled, and dispatched, and what it cost. Map production, equipment, fuel, spares, and dispatch records around your site so operations and finance work from one set of numbers.",
    "audience": "Quarry operators, mine contractors, aggregate and mineral producers, and mining service companies",
    "points": [
      [
        "Production and stockpile quantities",
        "Record extraction, crushing or processing output, and stockpile balances by product and grade, with the weighbridge or survey references your site uses."
      ],
      [
        "Equipment, fuel, and spares",
        "Track fuel issues, maintenance, tyres, and spare parts against each machine so equipment cost per tonne can be reviewed."
      ],
      [
        "Contractors and haulage",
        "Capture contractor trips, tonnes hauled, and agreed rates, and reconcile contractor bills against recorded work."
      ],
      [
        "Dispatch, royalties, and compliance",
        "Link dispatch quantities to customer invoices and keep the references needed for royalty, licence, and environmental reporting in your country."
      ]
    ],
    "prepare": "Bring a production report, a weighbridge or dispatch summary, a fuel log, and a contractor invoice.",
    "productName": "SAP Business One",
    "productPath": "/products/sap",
    "image": "/static/img/industries/mining.jpg",
    "imageAlt": "Illustration of an open-pit quarry with a haul truck, excavator, and crusher plant",
    "focus": [
      "Production & stockpiles",
      "Equipment & fuel",
      "Contractors & dispatch"
    ],
    "solutionNote": "SAP Business One covers purchasing, inventory, fixed assets, and finance; production quantities, equipment costing, and royalty reporting are scoped as configuration or add-on work during discovery."
  },
  {
    "slug": "fmcg-distribution",
    "name": "FMCG distribution",
    "kicker": "Van sales, outlets & collections",
    "headline": "Follow every outlet order from the van to the bank.",
    "summary": "Fast-moving consumer goods distributors serve hundreds of outlets through van sales, pre-orders, and delivery routes. Keep outlet orders, stock on the van, invoices, cash and mobile-money collections, and salesperson activity in one record so the day reconciles.",
    "audience": "FMCG distributors, sub-distributors, and brand-owned distribution teams serving retail outlets",
    "points": [
      [
        "Routes, outlets, and visits",
        "Plan routes, record outlet visits, and keep each outlet's order history and credit position available to the salesperson."
      ],
      [
        "Van stock and invoicing",
        "Load stock to the van, invoice at the outlet, and return unsold stock with the differences explained."
      ],
      [
        "Cash, mobile money, and credit",
        "Record cash, mobile-money, and bank collections against invoices, and review outstanding outlet balances by route."
      ],
      [
        "Schemes, expenses, and targets",
        "Apply trade schemes and discounts consistently, capture route expenses, and review salesperson performance against targets."
      ]
    ],
    "prepare": "Bring a route plan, a day's van-sales summary, a collection report, and a sample outlet invoice.",
    "productName": "LITFusion",
    "productPath": "/products/lit-fusion",
    "image": "/static/img/industries/fmcg-distribution-v2.jpg",
    "imageAlt": "Representative FMCG distribution scene with packaged food, beverages, household goods, a delivery truck, and a salesperson confirming a retailer order on a tablet",
    "focus": [
      "Routes & outlets",
      "Van stock & invoicing",
      "Collections"
    ],
    "solutionNote": "LITFusion covers field sales, billing, collections, expenses, and salesperson activity. Where a distributor also needs full purchasing, warehouse, and finance control, SAP Business One is discussed as the back office."
  },
  {
    "slug": "commodity-export",
    "name": "Commodity trading & export",
    "kicker": "Sourcing, grading & shipment",
    "headline": "Know what was bought, graded, stored, and shipped, and at what margin.",
    "summary": "Exporters of coffee, cocoa, cashew, sesame, grains, pulses, and minerals buy from many suppliers, grade and process lots, store them, and ship against contracts in foreign currency. Connect supplier purchases, lot quality, warehouse stock, shipping documents, and contract margins.",
    "audience": "Commodity exporters, aggregators, processors, and trading houses",
    "points": [
      [
        "Supplier purchases and lots",
        "Record purchases from farmers, agents, and cooperatives with lot references, weights, moisture or grade results, and advances paid."
      ],
      [
        "Grading, processing, and losses",
        "Track cleaning, sorting, drying, or processing output and the losses at each stage so lot cost is understood."
      ],
      [
        "Warehousing and collateral",
        "Keep stock by warehouse and lot, with references for warehouse receipts, inspection certificates, and financed stock where applicable."
      ],
      [
        "Contracts, shipping, and currency",
        "Link sales contracts, shipments, bills of lading, and export documents to invoices in the contract currency, and review realised margin per contract."
      ]
    ],
    "prepare": "Bring a purchase register, a grading or quality sheet, a warehouse stock report, a sales contract, and a shipment file.",
    "productName": "SAP Business One",
    "productPath": "/products/sap",
    "image": "/static/img/industries/commodity-export.jpg",
    "imageAlt": "Illustration of stacked sacks and shipping containers on a quay with a gantry crane and a vessel",
    "focus": [
      "Supplier lots & grading",
      "Warehouse stock",
      "Contracts & shipping"
    ],
    "solutionNote": "SAP Business One handles multi-currency purchasing, inventory by warehouse and batch, and finance. Contract-level margin, quality results, and export documentation are scoped as configuration, reports, or add-on work during discovery."
  },
  {
    "slug": "beverages-and-breweries",
    "category": "manufacturing",
    "name": "Beverages & breweries",
    "kicker": "Brewing, packaging & empties",
    "headline": "Connect the brew, the bottle, the crate, and the invoice.",
    "summary": "Breweries and beverage plants run batch production, high-speed packaging, returnable bottles and crates, excise reporting, and a distribution network that settles in cash. Map the brew or batch record, packaging lines, full and empty stock, and distributor accounts so production, excise, and finance work from the same numbers.",
    "audience": "Breweries, soft-drink and water bottlers, juice and dairy beverage plants, and their distributors",
    "image": "/static/img/industries/breweries.svg",
    "imageAlt": "Illustration of a brewery with stainless steel brewing and fermentation tanks, a bottling line, and stacked crates ready for dispatch",
    "focus": [
      "Batch & brew records",
      "Packaging & empties",
      "Excise & distributors"
    ],
    "points": [
      [
        "Ingredients and brew or batch records",
        "Record malt, hops, sugar, concentrate, water treatment, and packaging materials against each brew or batch, with yields and losses at each stage."
      ],
      [
        "Packaging lines and finished stock",
        "Capture bottling, canning, or kegging output by pack size and line, with batch and best-before references carried to finished stock."
      ],
      [
        "Returnable bottles, crates, and kegs",
        "Track empties issued to and returned from distributors, deposits, and breakages, so returnable assets are reconciled rather than written off."
      ],
      [
        "Excise, distributors, and collections",
        "Keep the production and dispatch quantities your excise return needs, and manage distributor credit, deposits, and collections by route."
      ]
    ],
    "prepare": "Bring a brew or batch sheet, a packaging line report, a distributor statement showing empties and deposits, and your excise return.",
    "productName": "SAP Business One",
    "productPath": "/products/sap",
    "solutionNote": "SAP Business One provides batch production, inventory by warehouse and batch, and finance. Returnable-asset tracking, excise reporting, and distributor route settlement are scoped as configuration, reports, or add-on work during discovery; LITFusion can cover van sales and collections in the field."
  }
];
